// Pure helpers for the Budget Monitoring pages. ES module without Vue imports so that
// `node --test` (see src/utils/__tests__) and webpack can both load it.

const COLORS = { GREEN: "success", YELLOW: "warning", RED: "error", NO_BUDGET: "grey" };
const LABELS = { GREEN: "HIJAU", YELLOW: "KUNING", RED: "MERAH", NO_BUDGET: "N/A" };
const NAMES = { GREEN: "Hijau", YELLOW: "Kuning", RED: "Merah", NO_BUDGET: "Tanpa budget" };
const HEX = { GREEN: "#4CAF50", YELLOW: "#FB8C00", RED: "#E53935", NO_BUDGET: "#9E9E9E" };
const TINTS = { GREEN: "#E8F5E9", YELLOW: "#FFF8E1", RED: "#FFEBEE", NO_BUDGET: "#EEEEEE" };
const ROW_CLASSES = {
  GREEN: "row-green",
  YELLOW: "row-yellow",
  RED: "row-red",
  NO_BUDGET: "row-none",
};
const METHOD_LABELS = {
  ITEM_EQUAL: "Item sama",
  ITEM_EQUAL_SPLIT: "Item sama (bagi)",
  PANEL_LINK: "Panel code",
  PANEL_LINK_SPLIT: "Panel code (bagi)",
  PROPORTIONAL: "Proporsional",
  NONE: "Tanpa PO",
  // budget share of a job in a PO (§5.2)
  SINGLE_JOB: "Job tunggal",
  ITEM_LINK: "Item terhubung",
  EVEN_SPLIT: "Dibagi rata",
};
const MARGIN_SOURCE_LABELS = {
  CALC_VERSION: "estimator",
  QUOTE: "quotation",
  OVERRIDE: "override",
  NONE: "tidak ada",
};

export const BANDS = ["GREEN", "YELLOW", "RED", "NO_BUDGET"];
export const BAND_FILTER_ITEMS = [
  { text: "Semua", value: "" },
  { text: "Merah", value: "RED" },
  { text: "Kuning", value: "YELLOW" },
  { text: "Hijau", value: "GREEN" },
  { text: "Tanpa budget", value: "NO_BUDGET" },
];
export const NOTIFICATION_TYPE_META = {
  BUDGET_YELLOW: { band: "YELLOW", title: "Kuning", when: "saat PO masuk band kuning (80–100 %)" },
  BUDGET_RED: { band: "RED", title: "Merah", when: "saat PO masuk band merah (> 100 %)" },
  BUDGET_RECOVERED: {
    band: "GREEN",
    title: "Kembali hijau",
    when: "saat PO turun dari merah ke kuning / hijau",
  },
};
export const PLACEHOLDER_HELP = {
  "@PO_SALES": "sales PO di CRM",
  "@ASSIGNED_USER": "user yang ditugaskan ke PO di CRM (ejaan CRM, orang yang sama)",
};
export const NOTIFICATION_TYPES = ["BUDGET_YELLOW", "BUDGET_RED", "BUDGET_RECOVERED"];
export const RECIPIENT_PLACEHOLDERS = ["@PO_SALES", "@ASSIGNED_USER"];

export const bandColor = b => COLORS[b] || "grey";
export const bandLabel = b => LABELS[b] || "N/A";
/** Sentence-case band name for chips and copy ("Merah", "Tanpa budget"). */
export const bandName = b => NAMES[b] || "Tanpa budget";
export const bandHex = b => HEX[b] || "#9E9E9E";
export const isPlaceholder = e => typeof e === "string" && e.trim().startsWith("@");
export const bandTint = b => TINTS[b] || "#EEEEEE";
export const bandRowClass = b => ROW_CLASSES[b] || "row-none";
export const methodLabel = m => METHOD_LABELS[m] || m || "-";
export const methodColor = m =>
  ["ITEM_EQUAL", "PANEL_LINK"].includes(m) ? "green" : m === "NONE" ? "grey" : "orange";
export const isEstimatedMethod = m => ["ITEM_EQUAL_SPLIT", "PANEL_LINK_SPLIT"].includes(m);
/** A line is an estimate when it was split, or went proportionally to more than one PO (share < 1). */
export const isEstimatedLine = l =>
  !!l && (isEstimatedMethod(l.allocationMethod) || Number(l.allocationShare) < 1);
export const marginSourceLabel = s => MARGIN_SOURCE_LABELS[s] || s || "-";

export const formatIdr = n =>
  n === null || n === undefined || n === ""
    ? "—"
    : Math.round(Number(n))
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, ".");

export const formatPct = n =>
  n === null || n === undefined || n === ""
    ? "—"
    : `${Number(n)
        .toFixed(1)
        .replace(".", ",")} %`;

export const formatQty = n => {
  if (n === null || n === undefined || n === "") return "—";
  const v = Number(n);
  return Number.isInteger(v) ? String(v) : String(v).replace(".", ",");
};

const pad2 = n => String(n).padStart(2, "0");

/** Local calendar date (yyyy-mm-dd). A yyyy-mm-dd string is returned as is; epoch / ISO values use local time. */
export const formatDate = d => {
  if (!d) return "—";
  if (typeof d === "string" && /^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
  const t = new Date(d);
  if (Number.isNaN(t.getTime())) return String(d).substring(0, 10);
  return `${t.getFullYear()}-${pad2(t.getMonth() + 1)}-${pad2(t.getDate())}`;
};

export const isLowConfidence = (est, threshold) =>
  est !== null && est !== undefined && Number(est) > Number(threshold);

export const sumAmount = lines => (lines || []).reduce((s, l) => s + Number(l.amountIdr || 0), 0);

export const sumBy = (rows, key) =>
  (rows || []).reduce((s, r) => s + Number((r && r[key]) || 0), 0);

export const amountsMatch = (a, b) => Math.abs(Number(a || 0) - Number(b || 0)) < 1;

/** Points for the inline trend svg: ratio in % per snapshot, scaled to a width×height box. */
export const trendPoints = (history, width, height, maxPct = 120) => {
  const rows = (history || []).filter(
    h => h && h.ratioPercent !== null && h.ratioPercent !== undefined
  );
  if (rows.length === 0) return "";
  const step = rows.length === 1 ? 0 : width / (rows.length - 1);
  return rows
    .map((h, i) => {
      const pct = Math.min(Number(h.ratioPercent), maxPct);
      const x = rows.length === 1 ? width / 2 : i * step;
      const y = height - (pct / maxPct) * height;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
};

/** Local date + time (yyyy-mm-dd hh:mm). */
export const formatDateTime = d => {
  if (!d) return "—";
  const t = new Date(d);
  if (Number.isNaN(t.getTime()))
    return String(d)
      .substring(0, 16)
      .replace("T", " ");
  return `${formatDate(t)} ${pad2(t.getHours())}:${pad2(t.getMinutes())}`;
};

/** Estimated share of a PO row's usage, 0..100. */
export const estimatedShare = row =>
  row && row.estimatedPercent !== null && row.estimatedPercent !== undefined
    ? Number(row.estimatedPercent)
    : 0;

/** "pasti 100 %" | "estimasi 35 %": how much of the usage was allocated proportionally. */
export const confidenceLabel = row => {
  if (!row || row.band === "NO_BUDGET") return "";
  const est = estimatedShare(row);
  return est > 0 ? `estimasi ${Math.round(est)} %` : `pasti 100 %`;
};

/** The band the PO had before the current one, from the ascending history. */
export const previousBand = history => {
  const rows = (history || []).filter(h => h && h.band);
  if (rows.length < 2) return null;
  const current = rows[rows.length - 1].band;
  for (let i = rows.length - 2; i >= 0; i--) {
    if (rows[i].band !== current) return rows[i];
  }
  return null;
};

/** Progress 0..1 of a RUNNING run, or null when the total is unknown. */
export const runProgress = run => {
  if (!run || run.status !== "RUNNING") return null;
  const total = Number(run.jobCount || 0);
  if (!total) return null;
  return Math.min(1, Number(run.processedJobs || 0) / total);
};

/** Per-band counts as the summary strip wants them, with zeros for missing bands. */
export const bandCounts = summary => {
  const src = (summary && summary.bandCounts) || {};
  const out = {};
  BANDS.forEach(b => {
    out[b] = Number(src[b] || 0);
  });
  return out;
};

/** Projection: has the run produced a projected ratio for this PO / job row? */
export const hasProjection = row =>
  !!row && row.projectedRatioPercent !== null && row.projectedRatioPercent !== undefined;

/** POs predicted to move into a worse band (summary.predictedCounts), zeros when absent. */
export const predictedCounts = summary => {
  const src = (summary && summary.predictedCounts) || {};
  return { RED: Number(src.RED || 0), YELLOW: Number(src.YELLOW || 0) };
};

/** BOM projection helpers: a line's need / covered / remaining value in IDR. */
export const bomNeedIdr = l => Number((l && l.qtyGross) || 0) * Number((l && l.unitPrice) || 0);
export const bomCoveredQty = l =>
  Number((l && l.coveredMrQty) || 0) +
  Number((l && l.coveredPoQty) || 0) +
  Number((l && l.coveredPrQty) || 0);
export const bomHasPrice = l => !!l && l.priceSource !== "NONE" && Number(l.unitPrice || 0) > 0;
export const bomPriceSourceLabel = s =>
  s === "LINE_PO"
    ? "harga PO job ini"
    : s === "LINE_PR"
    ? "harga PR job ini"
    : s === "LINE_MR"
    ? "harga MR job ini"
    : s === "LAST_PRICE"
    ? "pembelian terakhir"
    : "tanpa harga";

/** Human label for the scheduler cron (Spring 6-field, Asia/Jakarta); the raw cron when unrecognised. */
export const scheduleLabel = cron => {
  if (!cron) return "—";
  const f = String(cron)
    .trim()
    .split(/\s+/);
  if (f.length !== 6) return cron;
  const [sec, min, hour, dom, , dow] = f;
  const hhmm =
    /^\d+$/.test(hour) && /^\d+$/.test(min)
      ? `${hour.padStart(2, "0")}:${min.padStart(2, "0")} WIB`
      : null;
  const DAYS = {
    MON: "Senin",
    TUE: "Selasa",
    WED: "Rabu",
    THU: "Kamis",
    FRI: "Jumat",
    SAT: "Sabtu",
    SUN: "Minggu",
  };
  if (sec === "0" && hour === "*" && dom === "*" && dow === "*")
    return min === "0" ? "Setiap jam (menit 00)" : `Setiap jam (menit ${min})`;
  if (sec === "0" && hhmm && dom === "*") {
    const range = dow.toUpperCase().match(/^([A-Z]{3})-([A-Z]{3})$/);
    if (dow === "*") return `Setiap hari ${hhmm}`;
    if (range && DAYS[range[1]] && DAYS[range[2]])
      return `${DAYS[range[1]]}–${DAYS[range[2]]} ${hhmm}`;
    if (DAYS[dow.toUpperCase()]) return `Setiap ${DAYS[dow.toUpperCase()]} ${hhmm}`;
  }
  return cron;
};

/** Chart geometry: y for a ratio % within a box whose top is maxPct. */
export const yFor = (pct, height, maxPct = 120) =>
  height - (Math.min(Math.max(Number(pct) || 0, 0), maxPct) / maxPct) * height;

/** Share (fraction) of a job in a PO's budget: from the share json, else derived from amounts. */
export const shareOf = (share, poBudgetIdr) => {
  if (share && share.share !== null && share.share !== undefined) return Number(share.share);
  const b = Number(poBudgetIdr || 0);
  return b > 0 ? Number(share.budgetIdr || 0) / b : null;
};
